import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { cn } from '@/lib/utils';
import { Plane, XCircle, FileText, Upload, X } from 'lucide-react';

const STATUS_VARIANT = { pending: 'warning', booked: 'success', cancelled: 'default' };
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'booked', label: 'Booked' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Collapse travel rows into trips. Rows sharing a groupId are ONE trip booked
// for several people, so the travel manager sees a single line instead of one
// per traveller. Mirrors api/shared/travelGroup.js — keep the two in step.
//
// The representative row carries the whole group on `_group` so the Employee
// cell can list everyone and Book/Cancel can act on the trip. The backend
// fans the action out across the groupId, so only one row needs sending.
function collapseTravelGroups(rows) {
  const out = [];
  const byGroup = new Map();
  for (const r of rows) {
    if (!r.groupId) { out.push(r); continue; }
    if (!byGroup.has(r.groupId)) {
      const rep = { ...r, _group: [r] };
      byGroup.set(r.groupId, rep);
      out.push(rep);
    } else {
      byGroup.get(r.groupId)._group.push(r);
    }
  }
  return out;
}

export default function TravelRequestsPage() {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [booking, setBooking] = useState(null); // request to book

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listTravelRequests();
      setRequests(res.requests || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = requests;
    if (filter !== 'all') list = list.filter((r) => r.status === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        r.requesterName?.toLowerCase().includes(s) ||
        r.requesterEmail?.toLowerCase().includes(s) ||
        r.departureLocation?.toLowerCase().includes(s) ||
        r.finalDestination?.toLowerCase().includes(s)
      );
    }
    return collapseTravelGroups(list);
  }, [requests, filter, search]);

  const handleCancel = async (r) => {
    const groupSize = r._group ? r._group.length : 1;
    const who = groupSize > 1
      ? `all ${groupSize} travellers on this trip`
      : r.requesterName;
    if (!confirm(`Cancel travel for ${who}?`)) return;
    try {
      await adminApi.cancelTravel(r.partitionKey, r.rowKey);
      toast.success('Travel request cancelled');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    {
      key: 'requesterName', label: 'Employee',
      render: (r) => r._group && r._group.length > 1 ? (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{r._group.length} travellers</span>
            <Badge variant="warning">group trip</Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            {r._group.map((t) => t.requesterName || t.userName).join(', ')}
          </div>
          <div className="text-xs text-muted-foreground">
            Requested by {r.requestedByName || '—'}
            {r._group.some((t) => !t.passportFileName) && (
              <span className="ml-1 text-warning">
                &middot; {r._group.filter((t) => !t.passportFileName).length} passport(s) outstanding
              </span>
            )}
          </div>
        </div>
      ) : (
        <div><div className="font-medium">{r.requesterName || r.userName}</div><div className="text-xs text-muted-foreground">{r.department}</div></div>
      ),
    },
    {
      key: 'route', label: 'Route',
      render: (r) => <div className="text-sm">{r.departureLocation} → {r.finalDestination}</div>,
    },
    {
      key: 'dates', label: 'Dates',
      render: (r) => <div className="text-xs text-muted-foreground">{r.departureDate} — {r.arrivalDate}</div>,
    },
    {
      key: 'booking', label: 'Booking',
      render: (r) => r.bookedAirline ? <span className="text-xs font-mono">{r.bookedAirline} {r.bookedFlightNumber}</span> : <span className="text-muted-foreground/40">—</span>,
    },
    {
      key: 'status', label: 'Status',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>,
    },
    {
      key: 'actions', label: '',
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          {r.status === 'pending' && (
            <>
              <Button size="sm" variant="primary" onClick={(e) => { e.stopPropagation(); setBooking(r); }}>
                <Plane className="h-3.5 w-3.5" /> Book
              </Button>
              <button onClick={(e) => { e.stopPropagation(); handleCancel(r); }} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {r.status === 'booked' && (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setBooking(r); }}>
              Edit Booking
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Travel Requests</h1>
          <p className="text-muted-foreground">
            {requests.length} total · {requests.filter((r) => r.status === 'pending').length} pending booking
          </p>
        </div>
      </div>

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input label="Search" placeholder="Name, route..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-[300px]" />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer', filter === f.value ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-input text-muted-foreground hover:bg-muted')}>{f.label}</button>
          ))}
        </div>
      </div>

      <DataTable columns={columns} rows={filtered} loading={loading} emptyMessage="No travel requests." keyFn={(r) => r.rowKey} onRowClick={(r) => setBooking(r)} />

      {booking && (
        <BookingModal request={booking} onClose={() => setBooking(null)} onSaved={() => { setBooking(null); load(); }} />
      )}
    </div>
  );
}

function BookingModal({ request, onClose, onSaved }) {
  const toast = useToast();
  const r = request;
  const [form, setForm] = useState({
    bookedAirline: r.bookedAirline || '',
    bookedFlightNumber: r.bookedFlightNumber || '',
    bookedDepartureTime: r.bookedDepartureTime || '',
    bookedArrivalTime: r.bookedArrivalTime || '',
    bookingReference: r.bookingReference || '',
  });
  const [saving, setSaving] = useState(false);
  // Booking-ticket PDF (boarding pass / itinerary). Optional — sent as
  // multipart only when a file is selected.
  const [ticketFile, setTicketFile] = useState(null);
  const existingTicketName = r.bookingTicketFileName || (r.bookingTicketBlobPath ? 'booking-ticket.pdf' : null);

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const hasPrefs = r.flightPref1Airline || r.flightPref2Airline || r.flightPref3Airline;

  const handleSave = async () => {
    if (!form.bookedAirline || !form.bookedFlightNumber) {
      toast.error('Airline and flight number are required');
      return;
    }
    setSaving(true);
    try {
      let payload = form;
      if (ticketFile) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('bookingTicket', ticketFile);
        payload = fd;
      }
      await adminApi.updateTravelBooking(r.partitionKey, r.rowKey, payload);
      toast.success('Booking confirmed — requester has been notified');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${r.departureLocation} → ${r.finalDestination}`} width="560px">
      <div className="space-y-5">
        {/* Request summary */}
        <div className="grid grid-cols-2 gap-3 text-sm rounded-lg bg-muted/30 p-4">
          <div><span className="text-xs text-muted-foreground">Employee</span><div className="font-medium">{r.requesterName || r.userName}</div></div>
          <div><span className="text-xs text-muted-foreground">Email</span><div>{r.requesterEmail || r.userEmail}</div></div>
          <div><span className="text-xs text-muted-foreground">Dates</span><div>{r.departureDate} — {r.arrivalDate}</div></div>
          {r.reason && <div><span className="text-xs text-muted-foreground">Reason</span><div>{r.reason}</div></div>}
        </div>

        {hasPrefs && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Flight Preferences</h4>
            <div className="space-y-1 text-sm">
              {[1, 2, 3].map((n) => {
                const a = r[`flightPref${n}Airline`];
                const num = r[`flightPref${n}Number`];
                if (!a && !num) return null;
                return <div key={n}>{n}. <span className="font-medium">{a}</span> {num && <span className="font-mono text-muted-foreground">{num}</span>}</div>;
              })}
            </div>
          </div>
        )}

        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">Booking Details</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label="Airline *" value={form.bookedAirline} onChange={(e) => set('bookedAirline', e.target.value)} placeholder="e.g. Emirates" />
              <Input label="Flight Number *" value={form.bookedFlightNumber} onChange={(e) => set('bookedFlightNumber', e.target.value)} placeholder="e.g. EK803" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Departure Time" value={form.bookedDepartureTime} onChange={(e) => set('bookedDepartureTime', e.target.value)} placeholder="e.g. 2026-05-01 14:30" />
              <Input label="Arrival Time" value={form.bookedArrivalTime} onChange={(e) => set('bookedArrivalTime', e.target.value)} placeholder="e.g. 2026-05-01 22:15" />
            </div>
            <Input label="Booking Reference" value={form.bookingReference} onChange={(e) => set('bookingReference', e.target.value)} placeholder="e.g. ABC123" />

            {/* Booking-ticket PDF (boarding pass / itinerary). Surfaced to
                the employee on the travel detail page, just like the
                passport scan. */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[13px] font-medium text-muted-foreground">Booking Ticket (PDF)</label>
              {ticketFile ? (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <span className="flex-1 truncate">{ticketFile.name}</span>
                  <button onClick={() => setTicketFile(null)} className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <label className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2 cursor-pointer hover:border-primary/30 hover:bg-muted/30 transition-all text-sm text-muted-foreground">
                    <Upload className="h-4 w-4" />
                    <span>{existingTicketName ? 'Replace ticket PDF' : 'Upload booking ticket PDF'}</span>
                    <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setTicketFile(e.target.files?.[0] || null)} />
                  </label>
                  {existingTicketName && (
                    <span className="text-xs text-muted-foreground">Currently attached: {existingTicketName}</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} disabled={saving || !form.bookedAirline || !form.bookedFlightNumber}>
            <Plane className="h-4 w-4" /> Confirm Booking
          </Button>
        </div>
      </div>
    </Modal>
  );
}
